import React from 'react';
import { Box, Popover, Typography } from '@mui/material';
import { MapStatusToColor } from '../utils/enum';
import { formatStatus } from '../utils/helperFunctions';

interface MouseOverPopoverProps {
  children: React.ReactNode;
  colorCode: string;
  projectName: string;
  projectStatus: string;
  projectHours: number;
  memberStatus: string;
}

const MouseOverPopover = ({
  children,
  colorCode,
  projectName,
  projectStatus,
  projectHours,
  memberStatus
}: MouseOverPopoverProps): JSX.Element => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = (): void => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl) && memberStatus === 'APPROVED';

  return (
    <>
      <Box
        aria-owns={open ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
      >
        {children}
      </Box>
      <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: 'none',
          '.MuiPopover-paper': {
            borderRadius: '12px',
            boxShadow: 'none'
          }
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '12px',
            backgroundColor: '#295DAB0D',
            padding: '14px',
            gap: '8px'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <Box
                sx={{
                  height: '14px',
                  width: '14px',
                  borderRadius: '100px',
                  backgroundColor: colorCode
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Be Vietnam Pro',
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                  fontWeight: 400,
                  color: '#1A1B1F'
                }}
              >
                {projectName}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '10px',
                border: '1px solid #E3E2E6',
                borderRadius: '8px',
                padding: '6px 16px 6px 8px'
              }}
            >
              <Box
                sx={{
                  height: '9px',
                  width: '9px',
                  borderRadius: '100px',
                  backgroundColor: MapStatusToColor(projectStatus)
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Be Vietnam Pro',
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                  fontWeight: 400,
                  color: '#1A1B1F'
                }}
              >
                {formatStatus(projectStatus)}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              minHeight: '38px',
              backgroundColor: '#295DAB14',
              borderRadius: '7px'
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Be Vietnam Pro',
                  fontSize: '16px',
                  lineHeight: '24px',
                  fontWeight: 500,
                  color: '#001E2F'
                }}
              >
                {'Allocation'}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'baseline'
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Be Vietnam Pro',
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                  fontWeight: 400,
                  color: '#1A1B1F'
                }}
              >
                {projectHours}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Be Vietnam Pro',
                  fontSize: '10px',
                  lineHeight: '24px',
                  letterSpacing: '0.5px',
                  fontWeight: 400,
                  color: '#1A1B1F'
                }}
              >
                {'hrs per week'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default MouseOverPopover;
